import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Globe, MessageCircle, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateToStartHere } from '../utils/navigation';

export function OtherServicesPage() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <>
      <Helmet>
        <title>Other Services - Priority AI Services</title>
        <meta
          name="description"
          content="Additional services for health clinics including websites, chatbots, and AI phone agents to help you grow your practice."
        />
      </Helmet>

      <div className="min-h-screen pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Other Services
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Additional solutions to help your clinic attract more clients and run more smoothly.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Websites */}
            <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-8 hover:border-blue-500/40 transition-all">
              <Globe className="w-12 h-12 text-blue-500 mb-4" />
              <h2 className="text-2xl font-bold text-white mb-4">Websites & Landing Pages</h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Simple, professional websites that make it easy for potential patients to find you, trust you, and book with you.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Clean, modern design that builds trust</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Mobile-friendly and fast loading</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Clear calls-to-action that drive bookings</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>SEO optimized for local search</span>
                </li>
              </ul>
            </div>

            {/* Chatbots */}
            <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-8 hover:border-blue-500/40 transition-all">
              <MessageCircle className="w-12 h-12 text-blue-500 mb-4" />
              <h2 className="text-2xl font-bold text-white mb-4">Website Chatbots</h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Smart chatbots that answer common questions and capture leads 24/7, even when your clinic is closed.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Answers FAQs about services and pricing</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Captures contact info from interested visitors</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Available 24/7 for after-hours inquiries</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Trained on your specific services and approach</span>
                </li>
              </ul>
            </div>

            {/* AI Phone Agents */}
            <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-8 hover:border-blue-500/40 transition-all">
              <Phone className="w-12 h-12 text-blue-500 mb-4" />
              <h2 className="text-2xl font-bold text-white mb-4">AI Phone Agents</h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                AI-powered phone answering that handles calls when you're busy, books appointments, and never misses a lead.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Answers calls when you're with patients</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Books appointments directly into your calendar</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Sounds natural and professional</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Captures all caller information automatically</span>
                </li>
              </ul>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-8 mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">How it works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                  1
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Discovery call</h3>
                <p className="text-gray-300">
                  We discuss what you need and how it fits your clinic.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                  2
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Custom build</h3>
                <p className="text-gray-300">
                  I build and customize the solution for your specific needs.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                  3
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Launch & support</h3>
                <p className="text-gray-300">
                  We go live and I provide ongoing support as needed.
                </p>
              </div>
            </div>
          </div>

          {/* Who This Is For */}
          <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-8 mb-16">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Who this is for</h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-gray-300 mb-6 leading-relaxed">
                These services are for clinic owners who:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Want a professional online presence but don't have the time or expertise to build it themselves</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Need to capture more leads from their website and phone calls</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Are tired of missing calls because they're too busy with patients</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Want technology that works for them, not another system to manage</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-blue-500/10 border border-blue-500/30 rounded-lg p-12">
            <h2 className="text-3xl font-bold text-white mb-4">Interested in any of these?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Fill out the Start Here form and let me know which service you're interested in. We'll discuss if it's a good fit for your clinic.
            </p>
            <button
              onClick={() => navigateToStartHere(navigate, location.pathname)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white text-lg font-semibold rounded-md hover:bg-blue-600 transition-colors"
            >
              Start Here
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
